// "Add sales channel" — a three-step wizard replacing the two inline
// add-forms that used to sit permanently at the bottom of the section.
//
//   1. Pick the platform.
//   2. Enter only what is needed to PROVE the connection.
//   3. Test. Nothing is written until the channel has answered.
//
// The ordering is the point. Creating first and testing afterwards
// leaves a half-configured channel behind whenever the credentials are
// wrong — something an operator then has to notice and clean up. Testing
// first makes a bad paste just a message on screen.
//
// Everything else — warehouse locations, webhooks, catalogue, SKU
// mapping, consolidation — stays on the channel's own card, because it
// only makes sense once the channel is real.

import { useEffect, useState } from "react";
import { Loader2, Wifi, WifiOff, X, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import shopifyLogo from "../../../images/shopify.svg";
import amazonLogo from "../../../images/amazon.svg";
import type { AmazonMarketplace, SalesChannelConfig } from "./types";

interface Props {
    onCreated: (channel: SalesChannelConfig) => void;
    onClose: () => void;
}

type Platform = "shopify" | "amazon";

interface ProbeResult {
    ok: boolean;
    error: string | null;
    latencyMs?: number;
    suggestedName?: string | null;
    shop?: { name?: string } | null;
    marketplace?: { name?: string } | null;
}

const AddSalesChannelModal = ({ onCreated, onClose }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();

    const [platform, setPlatform] = useState<Platform | null>(null);
    const [testing, setTesting] = useState(false);
    const [creating, setCreating] = useState(false);
    const [probe, setProbe] = useState<ProbeResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Shopify fields.
    const [shopDomain, setShopDomain] = useState("");
    const [accessToken, setAccessToken] = useState("");
    const [webhookSecret, setWebhookSecret] = useState("");

    // Amazon fields.
    const [marketplaces, setMarketplaces] = useState<AmazonMarketplace[]>([]);
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [sellerId, setSellerId] = useState("");
    const [marketplaceId, setMarketplaceId] = useState("");
    const [warehouse, setWarehouse] = useState("");
    const [lwaClientId, setLwaClientId] = useState("");
    const [lwaClientSecret, setLwaClientSecret] = useState("");
    const [refreshToken, setRefreshToken] = useState("");

    // Shared.
    const [displayName, setDisplayName] = useState("");

    useEffect(() => {
        if (platform !== "amazon") return;
        let cancelled = false;
        (async () => {
            try {
                const [mRes, wRes] = await Promise.all([
                    apiFetch("/admin/integrations/amazon/marketplaces"),
                    apiFetch("/settings", {}),
                ]);
                if (!cancelled && mRes.ok) {
                    const j = await mRes.json();
                    setMarketplaces(Array.isArray(j?.marketplaces) ? j.marketplaces : []);
                }
                if (!cancelled && wRes.ok) {
                    const rows = await wRes.json();
                    setWarehouses((Array.isArray(rows) ? rows : []).map((r) => r.warehouse));
                }
            } catch { /* the selects render empty */ }
        })();
        return () => { cancelled = true; };
    }, [platform]);

    // Any edit invalidates a previous pass — otherwise it would be
    // possible to prove one token and then save a different one.
    const invalidate = () => { if (probe) setProbe(null); };

    const canTest = platform === "shopify"
        ? Boolean(shopDomain.trim() && accessToken.trim())
        : Boolean(sellerId.trim() && marketplaceId && lwaClientId.trim()
            && lwaClientSecret.trim() && refreshToken.trim());

    // Warehouse is required for Amazon only, and only at the CREATE step:
    // an unrouted Amazon channel rejects every order it receives.
    const canCreate = Boolean(probe?.ok)
        && (platform !== "amazon" || Boolean(warehouse));

    const runTest = async () => {
        setTesting(true);
        setProbe(null);
        setError(null);
        try {
            const url = platform === "shopify"
                ? "/admin/integrations/shopify/test-credentials"
                : "/admin/integrations/amazon/test-credentials";
            const body = platform === "shopify"
                ? { shopDomain: shopDomain.trim(), accessToken: accessToken.trim() }
                : {
                    sellerId: sellerId.trim(),
                    marketplaceId,
                    lwaClientId: lwaClientId.trim(),
                    lwaClientSecret: lwaClientSecret.trim(),
                    refreshToken: refreshToken.trim(),
                };
            const res = await apiFetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok && json?.message) throw new Error(json.message);
            setProbe(json as ProbeResult);
            // Offer the name the platform itself uses, unless the
            // operator has already typed one.
            if (json?.ok && json?.suggestedName && !displayName.trim()) {
                setDisplayName(json.suggestedName);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setTesting(false);
        }
    };

    const create = async () => {
        if (readOnly) return;
        setCreating(true);
        setError(null);
        try {
            const url = platform === "shopify"
                ? "/admin/integrations/shopify"
                : "/admin/integrations/amazon";
            const body = platform === "shopify"
                ? {
                    shopDomain: shopDomain.trim(),
                    displayName: displayName.trim() || undefined,
                    // Carried over so the card opens ready rather than
                    // asking for the same token a second time.
                    accessToken: accessToken.trim(),
                    webhookSecret: webhookSecret.trim() || undefined,
                }
                : {
                    sellerId: sellerId.trim(),
                    marketplaceId,
                    warehouse,
                    displayName: displayName.trim() || undefined,
                    lwaClientId: lwaClientId.trim(),
                    lwaClientSecret: lwaClientSecret.trim(),
                    refreshToken: refreshToken.trim(),
                };
            const res = await apiFetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            if (json?.channel) {
                onCreated(json.channel);
                toast.success(t("add_channel_created", { name: json.channel.displayName }));
            }
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setCreating(false);
        }
    };

    const field = (
        label: string, hint: string | null, value: string,
        set: (v: string) => void, placeholder = "", type: "text" | "password" = "text",
    ) => (
        <div className="admin-integrations-field" key={label}>
            <label className="admin-integrations-field-label">{label}</label>
            {hint && <p className="admin-integrations-field-hint">{hint}</p>}
            <input
                type={type}
                className="admin-integrations-input"
                value={value}
                onChange={(e) => { set(e.target.value); invalidate(); }}
                placeholder={placeholder}
                autoComplete="off"
                spellCheck={false}
                disabled={testing || creating}
            />
        </div>
    );

    return (
        <div className="modal-overlay" onClick={onClose} role="presentation">
            <div
                className="modal-card admin-integrations-add-channel"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-channel-title"
            >
                <div className="admin-integrations-add-channel-head">
                    <h3 id="add-channel-title" className="admin-integrations-confirm-title">
                        {t("add_channel_title")}
                    </h3>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={onClose}
                        aria-label={t("close")}
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* --- Step 1: platform ----------------------------------- */}
                {!platform ? (
                    <>
                        <p className="admin-integrations-confirm-body">{t("add_channel_choose")}</p>
                        <div className="admin-integrations-platform-grid">
                            <button
                                type="button"
                                className="admin-integrations-platform-btn"
                                onClick={() => setPlatform("shopify")}
                            >
                                {/* alt="" — the name below is the label, so a
                                    described logo would just read twice. */}
                                <img
                                    className="admin-integrations-platform-logo"
                                    src={shopifyLogo}
                                    alt=""
                                />
                                <strong>{t("add_channel_shopify")}</strong>
                            </button>
                            <button
                                type="button"
                                className="admin-integrations-platform-btn"
                                onClick={() => setPlatform("amazon")}
                            >
                                <img
                                    className="admin-integrations-platform-logo"
                                    src={amazonLogo}
                                    alt=""
                                />
                                <strong>{t("add_channel_amazon")}</strong>
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="admin-integrations-confirm-body">
                            {platform === "shopify"
                                ? t("add_channel_shopify_hint")
                                : t("add_channel_amazon_hint")}
                        </p>

                        {/* --- Step 2: only what proves the connection ----- */}
                        {platform === "shopify" ? (
                            <>
                                {field(t("shopify_field_domain"), t("shopify_field_domain_hint"),
                                    shopDomain, setShopDomain, "my-store.myshopify.com")}
                                {field(t("shopify_field_access_token"), t("shopify_field_access_token_hint"),
                                    accessToken, setAccessToken, "shpat_…", "password")}
                                {field(t("shopify_field_webhook_secret"), t("add_channel_webhook_secret_hint"),
                                    webhookSecret, setWebhookSecret, "", "password")}
                            </>
                        ) : (
                            <>
                                {field(t("amazon_field_seller_id"), null, sellerId, setSellerId, "A1B2C3D4E5F6G7")}
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">
                                        {t("amazon_field_marketplace")}
                                    </label>
                                    <select
                                        className="admin-integrations-input"
                                        value={marketplaceId}
                                        onChange={(e) => { setMarketplaceId(e.target.value); invalidate(); }}
                                        disabled={testing || creating}
                                    >
                                        <option value="">{t("amazon_choose_marketplace")}</option>
                                        {marketplaces.map((m) => (
                                            <option key={m.id} value={m.id}>{m.label}</option>
                                        ))}
                                    </select>
                                </div>
                                {field(t("amazon_field_client_id"), t("amazon_field_client_id_hint"),
                                    lwaClientId, setLwaClientId, "amzn1.application-oa2-client…")}
                                {field(t("amazon_field_client_secret"), null,
                                    lwaClientSecret, setLwaClientSecret, "", "password")}
                                {field(t("amazon_field_refresh_token"), t("amazon_field_refresh_token_hint"),
                                    refreshToken, setRefreshToken, "Atzr|…", "password")}
                            </>
                        )}

                        {/* --- Step 3: prove it --------------------------- */}
                        <div className="admin-integrations-test-row">
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={runTest}
                                disabled={!canTest || testing || creating}
                            >
                                {testing
                                    ? <><Loader2 size={14} className="admin-integrations-spinner" /> {t("integrations_test_running")}</>
                                    : <><Wifi size={14} /> {t("integrations_test_connection")}</>}
                            </button>
                            {probe && (
                                <span
                                    className={probe.ok
                                        ? "admin-integrations-test-result admin-integrations-test-result--ok"
                                        : "admin-integrations-test-result admin-integrations-test-result--fail"}
                                >
                                    {probe.ok
                                        ? <><CheckCircle2 size={14} /> {t("add_channel_test_ok", {
                                            name: probe.shop?.name ?? probe.marketplace?.name ?? "",
                                        })}</>
                                        : <><WifiOff size={14} /> {t("integrations_test_failure", { reason: probe.error ?? "?" })}</>}
                                </span>
                            )}
                        </div>

                        {/* Everything below only appears once the channel
                            has actually answered. */}
                        {probe?.ok && (
                            <>
                                {field(
                                    platform === "shopify" ? t("shopify_field_display_name") : t("amazon_field_display_name"),
                                    null, displayName, setDisplayName,
                                )}
                                {platform === "amazon" && (
                                    <div className="admin-integrations-field">
                                        <label className="admin-integrations-field-label">
                                            {t("amazon_field_warehouse")}
                                        </label>
                                        <p className="admin-integrations-field-hint">{t("amazon_routing_hint")}</p>
                                        <select
                                            className="admin-integrations-input"
                                            value={warehouse}
                                            onChange={(e) => setWarehouse(e.target.value)}
                                            disabled={creating}
                                        >
                                            <option value="">{t("amazon_choose_warehouse")}</option>
                                            {warehouses.map((w) => (
                                                <option key={w} value={w}>{w}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <p className="admin-integrations-field-hint">
                                    {t("add_channel_next_steps")}
                                </p>
                            </>
                        )}

                        {error && <p className="admin-integrations-field-error">{error}</p>}

                        <div className="admin-integrations-confirm-actions">
                            <button
                                type="button"
                                className="btn btn-sm btn-ghost"
                                onClick={() => { setPlatform(null); setProbe(null); setError(null); }}
                                disabled={creating}
                            >
                                {t("back")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={create}
                                disabled={!canCreate || creating || readOnly}
                                title={readOnly ? t("read_only_mode") : (probe?.ok ? undefined : t("add_channel_test_first"))}
                            >
                                {creating
                                    ? <Loader2 size={14} className="admin-integrations-spinner" />
                                    : t("add_channel_create")}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AddSalesChannelModal;
